import { CalendarDays } from 'lucide-react';
import type { IImplementationTimeline, ImplementationDayStatus } from '@/types/checklist.types';

const DONE_BLUE = '#1848FD';
const OVERDUE_RED = '#DC2626';
const NOT_STARTED_LIGHT = '#D1D5DB'; // gray-300; dark mode handled via Tailwind class

const dotFill = (status: ImplementationDayStatus): string => {
  switch (status) {
    case 'done':
      return DONE_BLUE;
    case 'running':
      return 'transparent';
    case 'overdue':
      return OVERDUE_RED;
    default:
      return NOT_STARTED_LIGHT;
  }
};

const connectorColor = (status: ImplementationDayStatus): string => {
  switch (status) {
    case 'done':
      return DONE_BLUE;
    case 'overdue':
      return OVERDUE_RED;
    default:
      return NOT_STARTED_LIGHT;
  }
};

const dotBorderColor = (status: ImplementationDayStatus): string => (status === 'overdue' ? OVERDUE_RED : DONE_BLUE);

const Legend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs">
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: DONE_BLUE }} />
      <span className="text-gray-600 dark:text-gray-300">Selesai</span>
    </span>
    <span className="flex items-center gap-1.5">
      <span
        className="h-2.5 w-2.5 rounded-full border-2 bg-white dark:bg-gray-900"
        style={{ borderColor: DONE_BLUE }}
      />
      <span className="text-gray-600 dark:text-gray-300">Sedang Berjalan</span>
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-gray-600" />
      <span className="text-gray-600 dark:text-gray-300">Belum Dimulai</span>
    </span>
  </div>
);

interface ImplementationTimelineCardProps {
  timeline: IImplementationTimeline;
}

// Tick positions shown on the axis below the rail (as day numbers).
const buildAxisTicks = (totalDays: number): number[] => {
  if (totalDays <= 1) return [0, totalDays];
  const ticks: number[] = [0];
  // Add ~3 intermediate ticks at round intervals.
  const step = Math.ceil(totalDays / 4);
  for (let t = step; t < totalDays; t += step) {
    ticks.push(t);
  }
  ticks.push(totalDays);
  return [...new Set(ticks)];
};

export const ImplementationTimelineCard = ({ timeline }: ImplementationTimelineCardProps) => {
  const { total_days, current_day, days, progress } = timeline;
  const axisTicks = buildAxisTicks(total_days);

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Implementasi AOS</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Program penerapan selama {total_days} hari</p>
        </div>
        <Legend />
      </div>

      {/* Current day summary */}
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-gray-800">
          <CalendarDays size={20} />
        </span>
        <div>
          <p className="text-base font-semibold text-gray-900 dark:text-white">
            Hari ke-{current_day} dari {total_days} hari
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Tahap: Implementasi AOS</p>
        </div>
      </div>

      {/* Day rail */}
      <div className="overflow-x-auto pb-8 pt-10 px-4">
        {/* dots + connectors */}
        <div className="flex w-full min-w-max items-center">
          {days.map((day, index) => {
            const isCurrent = day.day === current_day;
            const isLast = index === days.length - 1;
            const fill = dotFill(day.status);
            const connectorHex = !isLast ? connectorColor(day.status) : null;

            // Find if this dot needs an axis tick
            const ticksForThisDay = axisTicks.filter(
              tick => (tick === 0 ? 0 : Math.min(tick - 1, days.length - 1)) === index,
            );
            const tickToDisplay = ticksForThisDay.length > 0 ? ticksForThisDay[0] : null;

            return (
              <div key={day.day} className={`relative flex items-center ${!isLast ? 'flex-1' : ''}`}>
                {/* Dot Container */}
                <div className="relative flex flex-col items-center">
                  {/* "HARI KE-X" label above the current dot */}
                  {isCurrent && (
                    <div className="absolute bottom-full mb-2 flex flex-col items-center">
                      <div
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white whitespace-nowrap"
                        style={{ backgroundColor: DONE_BLUE }}
                      >
                        HARI KE-{current_day}
                      </div>
                      {/* Caret */}
                      <div
                        className="h-0 w-0 border-x-4 border-t-4 border-x-transparent mt-0.5"
                        style={{ borderTopColor: DONE_BLUE }}
                      />
                    </div>
                  )}

                  {/* Dot */}
                  <span
                    title={`Hari ${day.day} — ${day.date}`}
                    className={`relative z-10 h-3.5 w-3.5 shrink-0 rounded-full border-2 transition-transform hover:scale-125 ${
                      day.status === 'upcoming'
                        ? 'border-gray-300 bg-gray-300 dark:border-gray-600 dark:bg-gray-600'
                        : ''
                    }`}
                    style={
                      day.status !== 'upcoming'
                        ? {
                            backgroundColor: fill,
                            borderColor: dotBorderColor(day.status),
                            boxShadow: isCurrent ? `0 0 0 3px ${DONE_BLUE}33` : undefined,
                          }
                        : undefined
                    }
                  />

                  {/* Axis Tick Label below */}
                  {tickToDisplay !== null && (
                    <span className="absolute top-full mt-2 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {tickToDisplay}
                    </span>
                  )}
                </div>

                {/* Connector line to the right */}
                {!isLast && (
                  <div
                    className={`h-[3px] flex-1 min-w-[20px] ${
                      day.status === 'upcoming' ? 'bg-gray-300 dark:bg-gray-600' : ''
                    }`}
                    style={
                      day.status !== 'upcoming' ? { backgroundColor: connectorHex ?? NOT_STARTED_LIGHT } : undefined
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0 opacity-60">
            <path
              d="M1 7h1M3.5 3.5L4.2 4.2M7 1v1M10.5 3.5l-.7.7M13 7h-1M10.5 10.5l-.7-.7M7 13v-1M3.5 10.5l.7-.7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Arahkan kursor ke titik tanggal untuk melihat status harian.
        </span>
        <span className="font-semibold text-[#1848FD]">{progress.percentage}% Selesai</span>
      </div>
    </>
  );
};
